import { useEffect, useMemo, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { ListPage, ListRow, dataModelIcons } from '/src/components';
import { useAlertStore } from '/src/context';
import { mediumDate } from '/src/lib/constants';
import { DataModelPluralType, DataModelType } from '/src/lib/types';

/**
 * List Alerts Page
 */
export const ListAlertsPage: React.FC = observer(() => {
  const defaultSortType = 'effectiveDate';
  const defaultSortReverse = true;

  /** Context **/
  const alertStore = useAlertStore();

  /** State **/
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);

  /** Computed **/
  const alerts = alertStore.alerts;

  const listData = useMemo(() => {
    if (alerts) {
      const list = alerts.toSorted((a, b) => (get(a, sortType) > get(b, sortType) ? 1 : -1));
      if (sortReverse) list.reverse();

      return list;
    } else return [];
  }, [alerts, sortType, sortReverse]);

  /** Methods **/

  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /** Effects **/

  useEffect(() => {
    alertStore.query();
  }, []);

  /** Render **/

  return (
    <ListPage
      data={listData}
      modelType={DataModelType.Alert}
      headerItems={[
        { flex: 10, propName: 'source', label: 'Type' },
        { flex: 10, propName: 'created', label: 'Date' },
        { flex: 15, propName: 'company', label: 'Company' },
        { flex: 15, propName: 'loan', label: 'Deal' },
        { flex: 35, propName: 'message', label: 'Message' },
        { flex: 15, propName: 'sender', label: 'Originating User' },
      ]}
      sortType={sortType}
      sortReverse={sortReverse}
      sortList={sortList}
      hideCreateButton
    >
      {listData?.map((alert, i) => (
        <ListRow
          key={alert._id}
          items={[
            {
              flex: 10,
              propName: 'source',
              content: (
                <>
                  <Box component="span" alignItems="center" display="flex">
                    {(alert.source?.__t === DataModelPluralType.Payment ||
                      alert.message.includes(`${DataModelType.Payment}:`)) && (
                      <Tooltip title={DataModelType.Payment}>{dataModelIcons[DataModelType.Payment]}</Tooltip>
                    )}
                    {(alert.source?.__t === DataModelPluralType.Advance ||
                      alert.message.includes(`${DataModelType.Advance}:`)) && (
                      <Tooltip title={DataModelType.Advance}>{dataModelIcons[DataModelType.Advance]}</Tooltip>
                    )}
                    {(alert.source?.__t === DataModelPluralType.Asset ||
                      alert.message.includes(`${DataModelType.Asset}:`)) && (
                      <Tooltip title={DataModelType.Asset}>{dataModelIcons[DataModelType.Asset]}</Tooltip>
                    )}
                    {(alert.source?.__t === DataModelPluralType.Adjustment ||
                      alert.message.includes(`${DataModelType.Adjustment}:`)) && (
                      <Tooltip title={DataModelType.Adjustment}>{dataModelIcons[DataModelType.Adjustment]}</Tooltip>
                    )}
                  </Box>
                </>
              ),
            },
            { flex: 10, propName: 'created', content: dayjs(alert.created).format(mediumDate) },
            {
              flex: 15,
              propName: 'company',
              content: alert.source ? alertStore.getCompanyName(alert.source.company) : '',
              url: `/companies/${alert.source?.company}`,
            },
            {
              flex: 15,
              propName: 'loan',
              content: alert.source ? alertStore.getLoanName(alert.source.loan) : '',
              url: `/deals/${alert.source?.loan}`,
            },
            {
              flex: 35,
              propName: 'message',
              content: alert.message,
              url: alert.source ? `/${alert.source?.__t}/${alert.source?._id}` : undefined,
            },
            { flex: 15, propName: 'sender', content: alert.sender?.displayName, url: `/users/${alert.sender?._id}` },
          ]}
          rowIndex={i}
        />
      ))}
    </ListPage>
  );
});
